/**
 * The module-level home of every media-v3 upload in this tab.
 *
 * Jobs used to live in the state of whichever component called
 * `useMediaUpload()`, so switching a tab while a comment's files were still
 * going up threw the uploads away with the composer. Entries now live here,
 * keyed by a SCOPE: a component that mounts with the same named scope (a
 * composer's draft key) finds its uploads exactly where it left them, and the
 * root `MediaUploadTracker` keeps watching processing for every entry whether
 * or not its starter is still mounted.
 *
 * The store is data plus pure transitions — no I/O, no React. The runner that
 * performs the upload and the tracker that folds realtime events in are the
 * only writers.
 */

import { create } from "zustand";

import type { MediaUploadEntry, MediaUploadJob } from "../_types/media-upload";

export interface UploadStoreState {
  /** Newest first — the order `jobs` is exposed in. */
  entries: MediaUploadEntry[];
  add: (entries: MediaUploadEntry[]) => void;
  /** Replace one entry's job. A no-op for an unknown id (a job removed mid-flight). */
  patchJob: (
    jobId: string,
    update: (job: MediaUploadJob) => MediaUploadJob,
  ) => void;
  remove: (jobIds: string[]) => void;
  /** Drop every entry of a scope — a composer that sent, a form that reset. */
  clearScope: (scope: string) => void;
  /**
   * The hook owning an ephemeral scope unmounted. Its in-flight uploads keep
   * going (the tracker still sees them); they are dropped once settled.
   */
  releaseScope: (scope: string) => void;
  /** Drop orphaned entries that have nothing left to report. */
  prune: () => void;
}

/**
 * Nothing further can happen to this job: the upload failed, or the bytes are
 * processed AND the analysis is settled (the tracker settles a pending one
 * after its wait). Pure.
 */
export function isSettled(job: MediaUploadJob): boolean {
  if (job.phase === "failed") return true;
  return job.phase === "done" && job.analysis.status !== "pending";
}

/** Entries minus the orphaned ones that are settled. Pure. */
export function pruneEntries(entries: MediaUploadEntry[]): MediaUploadEntry[] {
  const kept = entries.filter(
    (entry) => !(entry.orphaned && isSettled(entry.job)),
  );
  return kept.length === entries.length ? entries : kept;
}

/** Mark a scope's entries orphaned, dropping those already settled. Pure. */
export function releaseScopeEntries(
  entries: MediaUploadEntry[],
  scope: string,
): MediaUploadEntry[] {
  return pruneEntries(
    entries.map((entry) =>
      entry.scope === scope && !entry.orphaned
        ? { ...entry, orphaned: true }
        : entry,
    ),
  );
}

export const useUploadStore = create<UploadStoreState>((set) => ({
  entries: [],
  add: (added) => set((state) => ({ entries: [...added, ...state.entries] })),
  patchJob: (jobId, update) =>
    set((state) => {
      const index = state.entries.findIndex((entry) => entry.job.id === jobId);
      if (index === -1) return state;
      const entry = state.entries[index];
      const next = [...state.entries];
      next[index] = { ...entry, job: update(entry.job) };
      return { entries: next };
    }),
  remove: (jobIds) =>
    set((state) => {
      const drop = new Set(jobIds);
      return {
        entries: state.entries.filter((entry) => !drop.has(entry.job.id)),
      };
    }),
  clearScope: (scope) =>
    set((state) => ({
      entries: state.entries.filter((entry) => entry.scope !== scope),
    })),
  releaseScope: (scope) =>
    set((state) => ({ entries: releaseScopeEntries(state.entries, scope) })),
  prune: () =>
    set((state) => {
      const pruned = pruneEntries(state.entries);
      return pruned === state.entries ? state : { entries: pruned };
    }),
}));

/** Entries of one scope, in store order. Pure. */
export function entriesInScope(
  entries: MediaUploadEntry[],
  scope: string,
): MediaUploadEntry[] {
  return entries.filter((entry) => entry.scope === scope);
}
